import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Lock, Phone, Building, UserPlus, LogIn, FileCheck, X, CreditCard, Award, CheckCircle, RefreshCw, Camera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DocumentCamera, compressImage } from '../components/DocumentCamera';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_OPTIONS = [
  { value: 'residence_card', labelKey: 'register.docTypeResidence', color: '#00A651' },
  { value: 'drivers_license', labelKey: 'register.docTypeLicense', color: '#2563eb' },
  { value: 'my_number_card', labelKey: 'register.docTypeMyNumber', color: '#7c3aed' },
];

// Reusable document upload component with file and camera options
const DocumentUpload = ({ label, file, filePath, uploading, onRemove, onCameraOpen, accentColor, required = false, testId }) => {
  const color = accentColor || '#00A651';
  const [preview, setPreview] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  return (
    <div className="flex-1">
      <div className="text-xs text-gray-600 mb-1 font-medium">{label}</div>
      {file ? (
        <div className="relative rounded-lg overflow-hidden h-[100px] border" style={{ borderColor: color }}>
          <img src={preview} alt={label} className="w-full h-full object-cover" />
          <button type="button" onClick={onRemove}
            className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center h-[100px] flex flex-col items-center justify-center">
          {uploading ? (
            <div className="animate-spin h-5 w-5 border-2 border-t-transparent rounded-full" style={{ borderColor: color, borderTopColor: 'transparent' }}></div>
          ) : (
            <button type="button" onClick={onCameraOpen}
              className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid={testId}>
              <Camera className="h-6 w-6" style={{ color }} />
              <span className="text-[10px] text-gray-500 mt-1">{t('register.camera')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function Register() {
  const { language, t } = useLanguage();
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  const modeParam = searchParams.get('mode');
  const [isLogin, setIsLogin] = useState(modeParam === 'login' ? true : true);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  
  // Document states
  const [documents, setDocuments] = useState({
    idFront: { file: null, path: '', uploading: false },
    idBack: { file: null, path: '', uploading: false },
    certFront: { file: null, path: '', uploading: false },
    certBack: { file: null, path: '', uploading: false }
  });
  
  // Document type selection
  const [idDocType, setIdDocType] = useState('');
  
  // Camera state: which slot is being captured
  const [cameraTarget, setCameraTarget] = useState(null);
  
  // File input refs (for qualification cert only)
  const certFrontRef = useRef(null);
  const certBackRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    nameKana: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: ''
  });

  // Set isLogin based on mode param
  useEffect(() => {
    if (modeParam === 'login') {
      setIsLogin(true);
    } else if (modeParam === 'register') {
      setIsLogin(false);
    }
  }, [modeParam]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    // If there's a redirect URL, go there instead of mypage
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate('/mypage');
    }
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('register.onlyImages'));
      return;
    }

    // Compress to 150PPI (no size limit)
    const compressed = await compressImage(file, idDocType || 'residence_card');

    setDocuments(prev => ({
      ...prev,
      [docType]: { ...prev[docType], file: compressed, uploading: true }
    }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', compressed);
      formDataUpload.append('docType', docType);

      const response = await axios.post(`${API}/api/users/upload-document`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setDocuments(prev => ({
        ...prev,
        [docType]: { file: compressed, path: response.data.path, uploading: false }
      }));
      toast.success(t('register.uploaded'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('register.uploadFailed'));
      setDocuments(prev => ({
        ...prev,
        [docType]: { file: null, path: '', uploading: false }
      }));
    }
  };

  const handleCameraCapture = (file) => {
    if (!cameraTarget) return;
    setCameraTarget(null);
    handleDocumentUpload(cameraTarget, file);
  };

  const removeDocument = (docType, inputRef) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: { file: null, path: '', uploading: false }
    }));
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      if (isLogin) {
        // Login
        const { useAuth } = await import('../contexts/AuthContext');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password })
        });
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'ログインに失敗しました');
        }
        
        localStorage.setItem('userToken', data.token);
        toast.success(t('register.loginSuccess'));
        // Redirect to specified URL or mypage
        window.location.href = redirectUrl || '/mypage';
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          toast.error(t('register.passwordsNotMatch'));
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error(t('register.passwordTooShort'));
          setLoading(false);
          return;
        }

        const result = await register({
          name: formData.name,
          nameKana: formData.nameKana,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          password: formData.password,
          idDocumentFront: documents.idFront.path,
          idDocumentBack: documents.idBack.path,
          certDocumentFront: documents.certFront.path,
          certDocumentBack: documents.certBack.path
        });
        
        // Check if email verification is required
        if (result.requiresVerification) {
          setRegistrationSuccess(true);
          setRegisteredEmail(formData.email);
        } else {
          toast.success(t('register.registrationSuccess'));
          // Redirect to specified URL or mypage
          navigate(redirectUrl || '/mypage');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || error.message || t('register.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/api/users/resend-verification`, { email: registeredEmail });
      toast.success(t('register.resendSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('register.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submit
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error(t('register.enterCode'));
      return;
    }
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/api/users/verify-email`, {
        email: registeredEmail,
        code: verificationCode
      });
      if (response.data.success) {
        setVerified(true);
        toast.success(t('register.verificationComplete'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('register.verificationFailed'));
    } finally {
      setVerifying(false);
    }
  };

  // Show email verification code input screen
  if (registrationSuccess) {
    if (verified) {
      return (
        <div className="min-h-screen bg-gray-50 py-12" data-testid="register-verified-page">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {t('register.verifiedTitle')}
              </h1>
              <p className="text-gray-600 mb-6">
                {t('register.verifiedDesc')}
              </p>
              <Link to="/register?mode=login" className="block" onClick={() => {
                setRegistrationSuccess(false);
                setVerified(false);
                setVerificationCode('');
                setIsLogin(true);
              }}>
                <Button className="w-full bg-[#00A651] hover:bg-[#008a43]">
                  {t('register.goToLoginButton')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 py-12" data-testid="register-success-page">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <Mail className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('register.verificationSent')}
            </h1>
            <p className="text-gray-600 mb-2">
              {t('register.verificationSentDesc')}
            </p>
            <p className="text-[#00A651] font-medium mb-6">{registeredEmail}</p>
            
            <div className="mb-6">
              <Label className="text-sm text-gray-700 mb-2 block text-left">
                {t('register.verificationCodeLabel')}
              </Label>
              <Input
                data-testid="verification-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-[0.5em] font-bold h-14"
              />
            </div>

            <div className="space-y-3">
              <Button
                data-testid="verify-code-button"
                onClick={handleVerifyCode}
                className="w-full bg-[#00A651] hover:bg-[#008a43]"
                disabled={verifying || verificationCode.length !== 6}
              >
                {verifying ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {t('register.verifying')}</>
                ) : (
                  t('register.verify')
                )}
              </Button>
              <Button 
                onClick={handleResendVerification}
                variant="outline"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {t('register.sending')}</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> {t('register.resendCode')}</>
                )}
              </Button>
              <Link to="/register?mode=login" className="block">
                <Button variant="ghost" className="w-full text-[#00A651]">
                  {t('register.goToLogin')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="register-page">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-6">
            <div className="bg-[#00A651] p-3 rounded-full">
              {isLogin ? <LogIn className="h-8 w-8 text-white" /> : <UserPlus className="h-8 w-8 text-white" />}
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            {isLogin 
              ? t('register.login')
              : t('register.createAccount')
            }
          </h1>
          <p className="text-center text-gray-500 mb-8">
            {isLogin
              ? t('register.loginSubtitle')
              : t('register.registerSubtitle')
            }
          </p>

          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <>
                {/* Name */}
                <div className="mb-4">
                  <Label htmlFor="name">
                    {t('register.name')} <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="pl-10"
                      placeholder={t('register.namePlaceholder')}
                      data-testid="input-name"
                    />
                  </div>
                </div>

                {/* Name Kana */}
                <div className="mb-4">
                  <Label htmlFor="nameKana">
                    {t('register.nameKana')}
                  </Label>
                  <Input
                    id="nameKana"
                    name="nameKana"
                    value={formData.nameKana}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder={t('register.nameKanaPlaceholder')}
                    data-testid="input-nameKana"
                  />
                </div>

                {/* Phone */}
                <div className="mb-4">
                  <Label htmlFor="phone">
                    {t('register.phone')}
                  </Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="090-1234-5678"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Company */}
                <div className="mb-4">
                  <Label htmlFor="company">
                    {t('register.company')}
                  </Label>
                  <div className="relative mt-1">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder={t('register.companyPlaceholder')}
                      data-testid="input-company"
                    />
                  </div>
                </div>

                {/* ID Document Upload */}
                <div className="mb-4">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-[#00A651]" />
                    {t('register.idDocument')} <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('register.idDocumentDesc')}
                  </p>

                  {/* Document type selector */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {DOC_TYPE_OPTIONS.map(dt => (
                      <button
                        key={dt.value}
                        type="button"
                        onClick={() => setIdDocType(dt.value)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          idDocType === dt.value
                            ? 'text-white border-transparent'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        }`}
                        style={idDocType === dt.value ? { backgroundColor: dt.color } : {}}
                        data-testid={`doctype-${dt.value}`}
                      >
                        {t(dt.labelKey)}
                      </button>
                    ))}
                  </div>

                  {!idDocType ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-400">
                      {t('register.selectDocType')}
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <DocumentUpload
                        label={t('register.front')}
                        file={documents.idFront.file}
                        filePath={documents.idFront.path}
                        uploading={documents.idFront.uploading}
                        onRemove={() => removeDocument('idFront')}
                        onCameraOpen={() => setCameraTarget('idFront')}
                        accentColor={DOC_TYPE_OPTIONS.find(d => d.value === idDocType)?.color}
                        required
                        testId="input-id-front"
                      />
                      <DocumentUpload
                        label={t('register.back')}
                        file={documents.idBack.file}
                        filePath={documents.idBack.path}
                        uploading={documents.idBack.uploading}
                        onRemove={() => removeDocument('idBack')}
                        onCameraOpen={() => setCameraTarget('idBack')}
                        accentColor={DOC_TYPE_OPTIONS.find(d => d.value === idDocType)?.color}
                        required
                        testId="input-id-back"
                      />
                    </div>
                  )}
                </div>

                {/* Qualification Certificate Upload */}
                <div className="mb-4">
                  <Label className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-[#00A651]" />
                    {t('register.qualCertificate')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('register.qualCertificateDesc')}
                  </p>
                  
                  <div className="flex gap-3">
                    <DocumentUpload
                      label={t('register.front')}
                      file={documents.certFront.file}
                      filePath={documents.certFront.path}
                      uploading={documents.certFront.uploading}
                      onFileChange={async (e) => {
                        const f = e.target.files[0];
                        if (f) handleDocumentUpload('certFront', f);
                      }}
                      onRemove={() => removeDocument('certFront', certFrontRef)}
                      inputRef={certFrontRef}
                      onCameraOpen={() => setCameraTarget('certFront')}
                      testId="input-cert-front"
                    />
                    <DocumentUpload
                      label={t('register.back')}
                      file={documents.certBack.file}
                      filePath={documents.certBack.path}
                      uploading={documents.certBack.uploading}
                      onFileChange={async (e) => {
                        const f = e.target.files[0];
                        if (f) handleDocumentUpload('certBack', f);
                      }}
                      onRemove={() => removeDocument('certBack', certBackRef)}
                      inputRef={certBackRef}
                      onCameraOpen={() => setCameraTarget('certBack')}
                      testId="input-cert-back"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Email */}
            <div className="mb-4">
              <Label htmlFor="email">
                {t('register.email')} <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="pl-10"
                  placeholder="example@email.com"
                  data-testid="input-email"
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-4">
              <Label htmlFor="password">
                {t('register.password')} <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="input-password"
                />
              </div>
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('register.passwordMinLength')}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            {!isLogin && (
              <div className="mb-6">
                <Label htmlFor="confirmPassword">
                  {t('register.confirmPassword')} <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="••••••••"
                    data-testid="input-confirmPassword"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || Object.values(documents).some(d => d.uploading) || (!isLogin && (!idDocType || !documents.idFront.path || !documents.idBack.path))}
              className="w-full bg-[#00A651] hover:bg-[#008a43] py-6"
              data-testid="submit-button"
            >
              {loading ? (
                <span className="animate-spin mr-2">⏳</span>
              ) : isLogin ? (
                <LogIn className="h-5 w-5 mr-2" />
              ) : (
                <UserPlus className="h-5 w-5 mr-2" />
              )}
              {isLogin
                ? t('register.loginButton')
                : t('register.registerButton')
              }
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-colors"
              style={{
                borderColor: isLogin ? '#00A651' : '#6b7280',
                color: isLogin ? '#00A651' : '#6b7280',
                backgroundColor: isLogin ? '#00A65108' : '#6b728008',
              }}
            >
              {isLogin
                ? <><UserPlus className="h-4 w-4" /> {t('register.switchToRegister')}</>
                : <><LogIn className="h-4 w-4" /> {t('register.switchToLogin')}</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Camera overlay with guide frame */}
      {cameraTarget && (
        <DocumentCamera
          docType={idDocType || 'residence_card'}
          side={cameraTarget.includes('Back') ? 'back' : 'front'}
          onCapture={handleCameraCapture}
          onClose={() => setCameraTarget(null)}
        />
      )}
    </div>
  );
}
